/* eslint-disable react/style-prop-object */
import { FormattedNumber } from 'react-intl';
import { PureComponent } from 'react';
import { toNumber } from 'es-toolkit/compat';

import clsx from 'clsx';

import { DisplayFormatsConsumer } from '@tock/coretypes';
import { displayInDollars, isDecimalCurrency } from './money';

export interface DisplayMoneyProps {
  amount?: number | null;
  className?: string;
  currencyCodeFromProps?: string;
  digitsToDisplay?: number;
  roundIfWholeNumber?: boolean;
  'data-testid'?: string;
}

/**
 * Displays an amount of money with the currency used by the business.
 */
export class DisplayMoney extends PureComponent<DisplayMoneyProps> {
  static defaultProps: Partial<DisplayMoneyProps> = {
    digitsToDisplay: 2,
    roundIfWholeNumber: false,
  };

  getDigitsToDisplay(currencyCode: string) {
    const { amount, digitsToDisplay: digitsToDisplayFromProps, roundIfWholeNumber } = this.props;

    const round = isDecimalCurrency(currencyCode) ? roundIfWholeNumber : true;

    return round && (amount || amount === 0) && amount % 100 === 0 ? 0 : digitsToDisplayFromProps;
  }
  // Setting the locale to es-mx for a business does not show the currency as MX$.
  // The prior workaround was to force Mexico businesses to use en-us.
  // FormattedNumbers locales parameter doesn't appear to work, so this is a new workaround.
  createNewIntlNumberFormat(currencyCode: string, digits: number | undefined, amount: number) {
    const options: Intl.NumberFormatOptions = {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
    };
    return new Intl.NumberFormat('en-US', options).format(toNumber(displayInDollars(amount)));
  }

  render() {
    const { amount, currencyCodeFromProps, className } = this.props;

    return (
      <DisplayFormatsConsumer>
        {(currency: string, locale: string) => {
          const currencyCode = currencyCodeFromProps || currency;
          const digits = this.getDigitsToDisplay(currencyCode);
          const displayMXInCurrency = currencyCode === 'MXN' && locale === 'es-mx';
          return (
            <span
              className={clsx('DisplayMoney', className)}
              data-testid={this.props['data-testid']}
            >
              {(amount != null) && !Number.isNaN(amount) && !displayMXInCurrency && (
                <FormattedNumber
                  currency={currencyCode}
                  maximumFractionDigits={digits}
                  minimumFractionDigits={digits}
                  style="currency"
                  value={toNumber(displayInDollars(amount))}
                />
              )}
              {(amount != null) &&
                !Number.isNaN(amount) &&
                displayMXInCurrency &&
                this.createNewIntlNumberFormat(currencyCode, digits, amount)}
            </span>
          );
        }}
      </DisplayFormatsConsumer>
    );
  }
}
